/* eslint-disable jsx-a11y/no-static-element-interactions */
import { BarChart3, ChevronLeft, ChevronRight } from 'lucide-react';
import { MouseEvent, useEffect, useMemo, useRef, useState } from 'react';

import { MedicalProvider } from '../../../services/types/treatment-types';
import { Button } from '../../ui/catalyst/button';
import { Heading } from '../../ui/catalyst/heading';
import { TimelineControls } from './TimelineControls';
import { TimelineHeader } from './TimelineHeader';
import { TimelineLane } from './TimelineLane';
import {
  calculateFullTimeRange,
  calculateVisibleTimeRange,
  getAllEncountersSorted,
  groupEncountersByProvider,
  isValidDate,
  parseDate,
} from './utils';

// CSS Animations for the timeline component
const timelineAnimations = `
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  @keyframes slideIn {
    from { transform: translateY(10px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
  
  @keyframes tooltipFadeIn {
    from { opacity: 0; transform: translateY(5px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  @keyframes pulse {
    0% { opacity: 0.6; }
    50% { opacity: 1; }
    100% { opacity: 0.6; }
  }
`;

interface TreatmentTimelineProps {
  matterId: string;
  providers: MedicalProvider[];
  isLoading: boolean;
  error: any;
  onProviderSelect: (providerId: string) => void;
}

const MIN_ZOOM = 10; // Represents the most zoomed-out view
const MAX_ZOOM = 100; // Represents the most zoomed-in view
const PAN_STEP_PERCENT = 25; // How much to pan with the arrow buttons (% of visible range)

export const TreatmentTimeline = ({
  matterId,
  providers,
  isLoading,
  error,
  onProviderSelect,
}: TreatmentTimelineProps) => {
  const [zoomLevel, setZoomLevel] = useState(MIN_ZOOM + 10); // Start slightly zoomed in
  const [showGaps, setShowGaps] = useState(true);
  const [gapThresholdDays, setGapThresholdDays] = useState(30);
  const [selectedEncounterId, setSelectedEncounterId] = useState<string | null>(
    null,
  );
  const [centerDate, setCenterDate] = useState<Date | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // For drag/pan functionality
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartDate, setDragStartDate] = useState<Date | null>(null);
  const timelineRef = useRef<HTMLDivElement>(null);

  // Calculate the full date range once from all providers
  const fullTimeRange = useMemo(
    () => calculateFullTimeRange(providers, 3),
    [providers],
  );

  // Calculate the visible window based on zoom level and center date
  const [visibleStartDate, visibleEndDate] = useMemo(() => {
    return calculateVisibleTimeRange(
      fullTimeRange[0],
      fullTimeRange[1],
      zoomLevel,
      centerDate,
    );
  }, [fullTimeRange, zoomLevel, centerDate]);

  // When the full time range changes, reset to the center
  useEffect(() => {
    setCenterDate(null); // Reset to center of full range
  }, [fullTimeRange]);

  // Add animation styles to document head
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = timelineAnimations;
    document.head.appendChild(styleElement);

    // Set initial load animation
    setTimeout(() => {
      setIsInitialLoad(false);
    }, 300);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  const allEncounters = useMemo(
    () => getAllEncountersSorted(providers),
    [providers],
  );

  const encountersByProvider = useMemo(
    () => groupEncountersByProvider(providers),
    [providers],
  );

  const providersWithEncounters = useMemo(() => {
    return providers.filter(
      (provider) =>
        provider.encounters &&
        provider.encounters.some((e) => e.date && isValidDate(e.date)),
    );
  }, [providers]);

  // Find the first and last encounter dates to use for scrolling to edges
  const firstEncounterDate = useMemo(() => {
    if (allEncounters.length === 0) return null;
    return allEncounters[0].date ? parseDate(allEncounters[0].date) : null;
  }, [allEncounters]);

  const lastEncounterDate = useMemo(() => {
    if (allEncounters.length === 0) return null;
    return allEncounters[allEncounters.length - 1].date
      ? parseDate(allEncounters[allEncounters.length - 1].date)
      : null;
  }, [allEncounters]);

  const handleSelectEncounter = (encounterId: string) => {
    // When selecting an encounter, find it and center the timeline on it
    const encounter = allEncounters.find((e) => e.id === encounterId);
    if (encounter?.date && typeof encounter.date === 'string') {
      const encounterDate = parseDate(encounter.date);
      setCenterDate(encounterDate);
    }

    setSelectedEncounterId(
      encounterId === selectedEncounterId ? null : encounterId,
    );
  };

  // Panning with arrow buttons
  const handlePanLeft = () => {
    const visibleDurationMs =
      visibleEndDate.getTime() - visibleStartDate.getTime();
    const panAmount = visibleDurationMs * (PAN_STEP_PERCENT / 100);
    const newCenterMs =
      (visibleStartDate.getTime() + visibleEndDate.getTime()) / 2 - panAmount;
    setCenterDate(new Date(newCenterMs));
  };

  const handlePanRight = () => {
    const visibleDurationMs =
      visibleEndDate.getTime() - visibleStartDate.getTime();
    const panAmount = visibleDurationMs * (PAN_STEP_PERCENT / 100);
    const newCenterMs =
      (visibleStartDate.getTime() + visibleEndDate.getTime()) / 2 + panAmount;
    setCenterDate(new Date(newCenterMs));
  };

  // Navigate to the beginning or end of the timeline
  const handleGoToStart = () => {
    if (firstEncounterDate) {
      setCenterDate(firstEncounterDate);
    }
  };

  const handleGoToEnd = () => {
    if (lastEncounterDate) {
      setCenterDate(lastEncounterDate);
    }
  };

  // Reset to show the full timeline
  const handleResetView = () => {
    setCenterDate(null);
    setZoomLevel(MIN_ZOOM + 10);
  };

  // Drag/pan functionality
  const handleMouseDown = (e: MouseEvent<HTMLDivElement>) => {
    if (e.button === 0) {
      // Left mouse button
      setIsDragging(true);
      setDragStartX(e.clientX);
      setDragStartDate(
        new Date((visibleStartDate.getTime() + visibleEndDate.getTime()) / 2),
      );

      // Prevent text selection during drag
      e.preventDefault();

      // Set cursor style
      if (timelineRef.current) {
        timelineRef.current.style.cursor = 'grabbing';
      }
    }
  };

  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    if (!isDragging || !dragStartDate) return;

    const deltaX = e.clientX - dragStartX;
    if (Math.abs(deltaX) < 5) return; // Small threshold to prevent accidental pans

    // Calculate pan amount based on delta and timeline width
    const timelineWidth = timelineRef.current?.offsetWidth || 1000;
    const visibleDurationMs =
      visibleEndDate.getTime() - visibleStartDate.getTime();

    // Convert pixel delta to time delta (negative because moving left should move timeline right)
    const timeDeltaMs = -(deltaX / timelineWidth) * visibleDurationMs;

    // Calculate new center date
    const newCenterMs = dragStartDate.getTime() + timeDeltaMs;
    setCenterDate(new Date(newCenterMs));
  };

  const handleMouseUp = () => {
    if (isDragging) {
      setIsDragging(false);
      if (timelineRef.current) {
        timelineRef.current.style.cursor = 'grab';
      }
    }
  };

  useEffect(() => {
    // Add global mouse up handler to end drag even if mouse is released outside the timeline
    const handleGlobalMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);
        if (timelineRef.current) {
          timelineRef.current.style.cursor = 'grab';
        }
      }
    };

    // Add the event listener
    document.addEventListener('mouseup', handleGlobalMouseUp);

    // Cleanup
    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDragging]);

  if (error) {
    return (
      <div className="text-sm text-red-500">Error loading treatment data.</div>
    );
  }

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center">
          <div className="h-6 w-40 bg-gray-200 rounded mb-4" />
          <div className="space-y-2">
            <div className="h-4 w-60 bg-gray-200 rounded" />
            <div className="h-4 w-48 bg-gray-200 rounded" />
            <div className="h-4 w-52 bg-gray-200 rounded" />
          </div>
        </div>
      </div>
    );
  }

  const hasEncounters = allEncounters.length > 0;

  return (
    <div className="h-full flex flex-col bg-white rounded-lg shadow-sm overflow-hidden">
      {/* Header and View Switcher */}
      <div className="flex justify-between items-center p-4 border-b border-gray-200">
        <div className="flex items-center">
          <Heading level={2} className="text-lg font-semibold text-gray-800">
            Treatment Timeline
          </Heading>
        </div>
      </div>

      <TimelineControls
        zoomLevel={zoomLevel}
        onZoomChange={setZoomLevel}
        minZoom={MIN_ZOOM}
        maxZoom={MAX_ZOOM}
        showGaps={showGaps}
        onToggleGaps={setShowGaps}
        gapThresholdDays={gapThresholdDays}
        onGapThresholdChange={setGapThresholdDays}
        encounterCount={allEncounters.length}
        providerCount={providersWithEncounters.length}
      />

      {/* Timeline Navigation Controls */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-100">
        <div className="flex items-center space-x-1">
          <Button
            plain
            className="p-1 rounded text-gray-500 hover:text-gray-700 hover:bg-gray-200 transition-colors"
            onClick={handleGoToStart}
            aria-label="Go to first encounter"
            disabled={!hasEncounters}
          >
            <ChevronLeft className="h-5 w-5" />
            <ChevronLeft className="h-5 w-5 -ml-3" />
          </Button>
          <Button
            plain
            className="p-1 rounded text-gray-500 hover:text-gray-700 hover:bg-gray-200 transition-colors"
            onClick={handlePanLeft}
            aria-label="Pan left"
            disabled={!hasEncounters}
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
        </div>

        <Button
          plain
          className="text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded px-2 py-1 transition-colors"
          onClick={handleResetView}
          aria-label="Reset view"
          disabled={!hasEncounters}
        >
          Reset View
        </Button>

        <div className="flex items-center space-x-1">
          <Button
            plain
            className="p-1 rounded text-gray-500 hover:text-gray-700 hover:bg-gray-200 transition-colors"
            onClick={handlePanRight}
            aria-label="Pan right"
            disabled={!hasEncounters}
          >
            <ChevronRight className="h-5 w-5" />
          </Button>
          <Button
            plain
            className="p-1 rounded text-gray-500 hover:text-gray-700 hover:bg-gray-200 transition-colors"
            onClick={handleGoToEnd}
            aria-label="Go to last encounter"
            disabled={!hasEncounters}
          >
            <ChevronRight className="h-5 w-5" />
            <ChevronRight className="h-5 w-5 -ml-3" />
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-hidden border-t border-gray-200">
        <div
          className="h-full overflow-x-auto overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent"
          ref={timelineRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          style={{
            minWidth: '800px',
            cursor: isDragging ? 'grabbing' : 'grab',
            userSelect: 'none', // Prevent text selection during drag
            WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
          }}
        >
          <div
            className={`relative will-change-transform ${isInitialLoad ? 'opacity-0' : 'opacity-100 transition-opacity duration-300'}`}
            style={{
              minHeight: `${Math.max(providersWithEncounters.length * 64 + 36, 200)}px`,
              width: '100%', // Ensure it takes full width
              animation: isInitialLoad ? 'none' : 'slideIn 0.3s ease-out',
            }}
          >
            <TimelineHeader
              startDate={visibleStartDate}
              endDate={visibleEndDate}
            />

            {providersWithEncounters.length === 0 ? (
              <div className="flex items-center justify-center h-40 bg-gray-50 text-sm text-gray-500">
                <div className="text-center p-4 rounded-lg bg-white shadow-sm border border-gray-100">
                  <div className="text-gray-400 mb-2">
                    <BarChart3 className="h-12 w-12 mx-auto opacity-40" />
                  </div>
                  No treatment data with dates available to display on the
                  timeline.
                </div>
              </div>
            ) : (
              <div className="relative">
                {/* Vertical grid lines for better reference */}
                <div className="absolute inset-0 pointer-events-none">
                  {[0, 25, 50, 75, 100].map((position) => (
                    <div
                      key={`gridline-${position}`}
                      className="absolute top-0 bottom-0 w-px bg-gray-100"
                      style={{ left: `${position}%` }}
                    />
                  ))}
                </div>

                {providersWithEncounters.map((provider, index) => {
                  if (!provider.id) return null;
                  const encounters =
                    encountersByProvider.get(provider.id) || [];
                  return (
                    <div
                      key={provider.id}
                      style={{
                        animation: isInitialLoad
                          ? 'none'
                          : `fadeIn 0.3s ease-out ${index * 0.05}s both`,
                      }}
                    >
                      <TimelineLane
                        provider={provider}
                        encounters={encounters}
                        startDate={visibleStartDate}
                        endDate={visibleEndDate}
                        selectedEncounterId={selectedEncounterId}
                        onSelectEncounter={handleSelectEncounter}
                        showGaps={showGaps}
                        gapThresholdDays={gapThresholdDays}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
